import { useEffect, useRef, useState } from "react";
import { Spinner } from "./Spinner";
import { FormattedMessage, useIntl } from "react-intl";
import { DataTable } from "./DataTable";
import { IFilterButtonData } from "./TableFilters";
import { getCompanies } from '../api/Company';
import { ICompany, IPomReportDataInfo, REPORTSTATUS } from "../util/DataModelInterface";
import { periodView, statusView } from "./CommonControls";
import { getReportStatus } from "../api/PomReport";
import { ToastWrap } from "./ToastWrap";
import useToastHandler from "../hooks/useToastHandler";
import { sendReminders } from "../api/Email";
import { Typeahead } from "react-bootstrap-typeahead";
import { getCurrentPeriodString, getPreviousPeriodString, getPeriodString, getDeadline } from "../util/FormatDateUtil";


interface IOngoingTableData {
  company: string,
  period: string,
  periodView: JSX.Element | string,
  state: JSX.Element,
  id: string,
  status: string,
  periodFilter: string,
  companyId: string,
  latest: string,
}

const ONGOING_FILTER = {
  ALL: 'all',
  CURRENT: 'current',
  PREVIOUS: 'previous',
  EARLIER: 'earlier',
}


export const ReportAdminOngoing = () => {

  const intl = useIntl();
  const tableRef = useRef<any>(null);
  const selectAllCheckboxRef = useRef<any>(null);

  const [loading, setLoading] = useState(false);
  const [currentPeriod] = useState(getCurrentPeriodString());
  const [previousPeriod] = useState(getPreviousPeriodString());
  const [filters, setFilters] = useState([] as IFilterButtonData[]);
  const [count, setCount] = useState(0);
  const [toastActive, setToastActive] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [countMax, setCountMax] = useState(0);
  const [selected, setSelected] = useState([] as IOngoingTableData[]);
  const [selectedFilter, setSelectedFilter] = useState(ONGOING_FILTER.ALL);
  const [reports, setReports] = useState([] as IPomReportDataInfo[]);
  const { addToast } = useToastHandler();
  const [companies, setCompanies] = useState([] as ICompany[]);
  const [reportTableData, setReportTableData] = useState([] as IOngoingTableData[]);
  const [selections, setSelections] = useState([] as ICompany[]);

  useEffect(() => {
    setLoading(true);
    getCompanies().then((data: any) => {
        if (data?.error) {
          addToast(data.error, 'companies', 'fetch');
        } else if (data) {
          setCompanies(data);
          return data;
        }
    }).then((d) => {
      refreshData(d).finally(() => {
        setLoading(false);
      });
    }).catch(() => {
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    parseTableData(reports, companies);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections]);

  useEffect(() => {
    // Control checkbox checked/indeterminate state...
    // If every/none item of the filtered items is selected, indeterminate = false
    // If part of items selected, indeterminate = true
    if (!selectAllCheckboxRef.current) {
      return;
    }
    if (count === 0) {
      selectAllCheckboxRef.current.checked = false;
      selectAllCheckboxRef.current.indeterminate = false;
    } else if (count < countMax ) {
      selectAllCheckboxRef.current.checked = false;
      selectAllCheckboxRef.current.indeterminate = true;
    } else {
      selectAllCheckboxRef.current.checked = true;
      selectAllCheckboxRef.current.indeterminate = false;
    }
  }, [count, selectedFilter, countMax]);

  const refreshData = async (companies: ICompany[]) => {

    await getReportStatus([]).then((data) => {
        if (data?.error) {
          addToast(data.error, 'reports', 'fetch');
        } else if (data) {
          setReports(data);
          parseTableData(data, companies);
        }
    });
  }

  const filter = (id: number) => {
    switch(id) {
      case 0:
        return ONGOING_FILTER.ALL;
      case 1:
        return ONGOING_FILTER.CURRENT;
      case 2:
        return ONGOING_FILTER.PREVIOUS;
      case 3:
        return ONGOING_FILTER.EARLIER;
      default:
        return ONGOING_FILTER.ALL;
    }
  }

  const handleSelectAll = (change: boolean) => {
    tableRef?.current?.toggleSelectedRows(change);
  };

  const toolBar = () => {
    return(
    <div className="toolbar">
      <div className="select-all me-auto">
        <div className="form-check"> 
          <input className="form-check-input" type="checkbox" name="selectall" id="selectall"
                 ref={selectAllCheckboxRef} onChange={(e) => handleSelectAll(e.target.checked)}/>
          <label className="form-check-label" htmlFor="selectall"><FormattedMessage id={'bookkeeping.select.all'}/></label>
          <span className ="select-status ms-4"><FormattedMessage id={'bookkeeping.selected.count'} values={{count: count}}/></span>
        </div>
      </div>
      <span className="item-actions ms-auto">
        <button key='toolbarbutton' className="btn btn btn-primary btn-download"
          disabled={selected.length === 0}
          onClick={() => { send(); }}>
          <i className="me-2 fa fa-paper-plane" aria-hidden="true"></i>
          <FormattedMessage id={'button.ongoing.send'}/>
        </button>
      </span>
    </div>);
  }

  const send = () => {
    setLoading(true);
    if(selected.length > 0) {
      setSentCount(selected.length);
      console.log(selected);
      const unique = [...new Set(selected.map(s => s.id))];
      sendReminders(unique.toString()).then((data: any) => {
        if (data?.error) {
          addToast(data.error, 'ongoing', 'send');
          setLoading(false);
        } else {
          setToastActive(true);
          refreshData(companies).finally(() => {
            setLoading(false);
          });
        }
    });
    }
  }

  const comparer = (a: any, b: any) => {
    return a.company.localeCompare(b.company);
  }

  const parseTableData = (reports: IPomReportDataInfo[], companies: ICompany[]) => {
    let data = [] as IOngoingTableData[];
    // 'company', 'period', 'state', 'latest'
    let all = 0;
    let current = 0;
    let previous = 0;
    let earlier = 0;
    reports.forEach((r, i) => {
      let compName = companies.find((c) => {return c.id === r.company})?.name;
      if (selections.length > 0 && !selections.find((c) => c.name === compName)) {
        // console.log("%s not in selections", compName);
      } else {
        let date = new Date(r.period);
        let period = getPeriodString(date);
        let monthStr = period.split("-")[1];
        let yearStr = period.split("-")[0];
        let reportingPeriod = monthStr + "/" + yearStr;
        let periodFilter = ONGOING_FILTER.ALL;
        if(r.status === REPORTSTATUS.NEW || r.status === REPORTSTATUS.ONGOING) {
          if(period === currentPeriod) {
            periodFilter = ONGOING_FILTER.CURRENT;
          }
          if(period === previousPeriod) {
            periodFilter = ONGOING_FILTER.PREVIOUS;
          }
          if(period !== previousPeriod && period !== currentPeriod) {
            periodFilter = ONGOING_FILTER.EARLIER;
          }
        }
        data[i] = {
          company: compName ? compName : '',
          period: reportingPeriod,
          periodView: periodView(reportingPeriod, r.report_interval),
          state: statusView(r.status, getDeadline(date)),
          latest: r.reminder ? r.reminder.replace(/T|[.].+/g, " ") : intl.formatMessage({id: 'ongoing.neversent'}),
          id: r.report_id,
          status: r.status,
          companyId: r.company,
          periodFilter: periodFilter
        };
        // NEW, ONGOING, CONFIRMED, BILLED, REOPENED, CORRECTED, REBILLED
        if(r.status === REPORTSTATUS.NEW || r.status === REPORTSTATUS.ONGOING) {
          all++;
          let period = getPeriodString(new Date(r.period));
          if(period === currentPeriod) current++;
          if(period === previousPeriod) previous++;
          if(period !== previousPeriod && period !== currentPeriod) earlier++;
        }
      }
    });
    setFilters([
      { button: ONGOING_FILTER.ALL, count: all,
        filters: [{column: 'status', filterValues: [REPORTSTATUS.NEW, REPORTSTATUS.ONGOING]},
                  {column: 'periodFilter', filterValues: []}] },
      { button: ONGOING_FILTER.CURRENT, count: current,
        filters: [{column: 'status', filterValues: [REPORTSTATUS.NEW, REPORTSTATUS.ONGOING]},
                  {column: 'periodFilter', filterValues: [ONGOING_FILTER.CURRENT]}] },
      { button: ONGOING_FILTER.PREVIOUS, count: previous,
        filters: [{column: 'status', filterValues: [REPORTSTATUS.NEW, REPORTSTATUS.ONGOING]},
                  {column: 'periodFilter', filterValues: [ONGOING_FILTER.PREVIOUS]}] },
      { button: ONGOING_FILTER.EARLIER, count: earlier,
        filters: [{column: 'status', filterValues: [REPORTSTATUS.NEW, REPORTSTATUS.ONGOING]},
                  {column: 'periodFilter', filterValues: [ONGOING_FILTER.EARLIER]}]}
    ]);
    data.sort(comparer);
    setReportTableData(data);
  }

  const foundItem = (sel: ICompany[]) => {
    let selItem = sel[0];
    console.log("found", selItem);
    setSelections(sel);
  }

  const filterByFields = ['business_id', 'name'];

  return (
    <div id="tab-tabpane-ongoing" role="tabpanel" aria-labelledby="tab-tab-ongoing">
      <Spinner active={loading} />
      <ToastWrap active={toastActive} heading={intl.formatMessage({id: 'ongoing.sent'})} 
                text={intl.formatMessage({id: 'ongoing.reminders.sent.info'}, {count: sentCount})} 
                type={'success'} setActive={(active: boolean) => setToastActive(active)} />
      <div className="search-group me-md-auto">
        <Typeahead
          id="typeahead-search-comp"
          inputProps={{
            'aria-label': intl.formatMessage({id: 'companies.search.text'}),
          }}
          clearButton
          filterBy={filterByFields}
          labelKey="name"
          onChange={(sel) => {foundItem(sel as ICompany[]);}}
          options={companies}
          placeholder={intl.formatMessage({id: 'companies.search.text'})}
          renderMenuItemChildren={(option) => (
            <div>
              {(option as ICompany).name}
              <div>
                <small>{(option as ICompany).business_id}</small>
              </div>
            </div>
          )}
        />
      </div>
      {reportTableData.length > 0 &&
      <DataTable  ref={tableRef}
                  tableheadings={['company', 'periodView', 'state', 'latest']}
                  filters={filters}
                  tabledata={reportTableData} caption={'ongoing.tablecaption'}
                  tableType={'ongoing'} tableId={'reportList'} 
                  toolbar={toolBar()}
                  showFilters={selections.length > 0 ? false : true}
                  selectHeader={intl.formatMessage({id:'ongoing.select'})}
                  setSelected={(items: IOngoingTableData[]) => {setSelected(items); setCount(items.length);}}
                  setTableMaxCount={(count: number) => setCountMax(count)}
                  filterSet={selectedFilter}
                  hiddenColumns={['id', 'status', 'companyId', 'periodFilter', 'period']}
                  optSortableColumns={['company', 'period']}
                  setSelectedButton={(id: number) => {setSelectedFilter(filter(id)); console.log(id);}}
                />
      }
    </div>
  );
};